import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, Power, PowerOff, Brain, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccessGate from "@/components/AccessGate";

/**
 * Firm-wide Coding Memory admin page.
 *
 * Every accountant correction and every "Confirm COA group" tick reinforces a rule
 * mapping a description pattern → COA. Once a rule reaches 2 hits, it's auto-applied
 * to matching transactions on the next AI coding pass (skipping Claude for those rows).
 *
 * Admin + Manager can view / disable / delete rules here.
 */

function StatChip({ label, value, tone }) {
  const toneCls = {
    default: "bg-[var(--surface)] text-[var(--ink-1)]",
    ok: "bg-[var(--ok)]/10 text-[var(--ok)]",
    warn: "bg-[var(--gold)]/15 text-[var(--gold)]",
    err: "bg-[var(--err)]/10 text-[var(--err)]",
  }[tone || "default"];
  return (
    <div className={`px-3 py-2 rounded border border-[var(--line)] ${toneCls}`} data-testid={`memory-stat-${label.toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-mono-data text-lg font-medium">{value}</div>
    </div>
  );
}

function MemoryTable() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | conflicted | disabled
  const [search, setSearch] = useState("");

  async function load() {
    try {
      const [r, s] = await Promise.all([
        api.get("/frtx/coding-memory"),
        api.get("/frtx/coding-memory/stats"),
      ]);
      setRules(r.data);
      setStats(s.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggle(rule) {
    setBusy(rule.id);
    try {
      await api.patch(`/frtx/coding-memory/${rule.id}`, { is_disabled: !rule.is_disabled });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setBusy("");
  }

  async function remove(rule) {
    if (!window.confirm(`Delete this rule?\n\n"${rule.pattern}" → ${rule.coa_name}\n\nThe next AI coding pass will fall back to Claude for matching descriptions.`)) return;
    setBusy(rule.id);
    try {
      await api.delete(`/frtx/coding-memory/${rule.id}`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setBusy("");
  }

  const visible = rules.filter((r) => {
    if (filter === "active" && !(!r.is_disabled && !r.is_conflicted && r.hit_count >= (stats?.auto_apply_min_hits || 2))) return false;
    if (filter === "conflicted" && !r.is_conflicted) return false;
    if (filter === "disabled" && !r.is_disabled) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(r.pattern.toLowerCase().includes(s) || r.coa_name.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="memory-page">
      <div className="mb-3">
        <Link to="/frtx" data-testid="memory-back-link" className="text-xs text-[var(--ink-2)] hover:text-[var(--ink-1)] inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Back to engagements
        </Link>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Practice OS · FRTX</div>
        <h1 className="font-display text-2xl flex items-center gap-2" data-testid="memory-title">
          <Brain size={22} /> Firm Coding Memory
        </h1>
        <p className="text-xs text-[var(--ink-2)] mt-1 max-w-2xl">
          Every time you re-code a transaction or confirm a COA group, the pattern is remembered here.
          Once a rule has {stats?.auto_apply_min_hits || 2}+ hits and no conflicts, the next AI coding pass will use it
          instead of calling Claude — faster, cheaper, and consistent across engagements.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl">
          <StatChip label="Total" value={stats.total} tone="default" />
          <StatChip label="Active" value={stats.active} tone="ok" />
          <StatChip label="Conflicted" value={stats.conflicted} tone="err" />
          <StatChip label="Disabled" value={stats.disabled} tone="warn" />
        </div>
      )}

      {err && <div className="text-xs text-[var(--err)]" data-testid="memory-err">{err}</div>}

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "conflicted", label: "Conflicted" },
          { key: "disabled", label: "Disabled" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`memory-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${filter === f.key ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--navy)]"}`}
          >
            {f.label}
          </button>
        ))}
        <input
          data-testid="memory-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search pattern or COA…"
          className="input-slim text-[11px] ml-auto min-w-[220px]"
        />
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead>
            <tr>
              <th>Pattern</th>
              <th>→ COA</th>
              <th className="text-right">Hits</th>
              <th>Status</th>
              <th>Examples</th>
              <th>Last Seen</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[var(--ink-2)] text-xs italic">
                {rules.length === 0
                  ? "No rules learned yet. Re-code a few transactions or confirm a COA group in any engagement and they'll appear here."
                  : "No rules match the current filter."}
              </td></tr>
            )}
            {visible.map((r) => {
              const isActive = !r.is_disabled && !r.is_conflicted && r.hit_count >= (stats?.auto_apply_min_hits || 2);
              return (
                <tr key={r.id} data-testid={`memory-row-${r.id}`} className={r.is_disabled ? "opacity-50" : ""}>
                  <td className="font-mono-data text-[11px]">{r.pattern}</td>
                  <td className="text-[11px] font-medium">{r.coa_name}</td>
                  <td className="text-right font-mono-data text-[11px]">{r.hit_count}</td>
                  <td>
                    {r.is_conflicted ? (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--err)]/10 text-[var(--err)]" title={`Same pattern was coded to a different COA ${r.conflict_count} time(s) — this rule won't auto-apply.`}>
                        <AlertTriangle size={9} /> Conflict
                      </span>
                    ) : r.is_disabled ? (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--gold)]/15 text-[var(--gold)]">Disabled</span>
                    ) : isActive ? (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--ok)]/10 text-[var(--ok)]">Active</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--surface)] text-[var(--ink-2)]" title={`Needs ${(stats?.auto_apply_min_hits || 2) - r.hit_count} more hit(s) to auto-apply.`}>
                        Learning
                      </span>
                    )}
                  </td>
                  <td className="text-[10px] text-[var(--ink-2)] max-w-[280px]">
                    <div className="truncate" title={(r.examples || []).join(" · ")}>
                      {(r.examples || []).slice(0, 2).join(" · ")}
                    </div>
                  </td>
                  <td className="text-[10px] text-[var(--ink-2)] whitespace-nowrap">{(r.last_seen_at || "").slice(0, 10)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      data-testid={`memory-toggle-${r.id}`}
                      onClick={() => toggle(r)}
                      disabled={busy === r.id}
                      className="text-[var(--ink-2)] hover:text-[var(--navy)] mr-2"
                      title={r.is_disabled ? "Enable this rule" : "Disable this rule"}
                    >
                      {r.is_disabled ? <PowerOff size={13} /> : <Power size={13} />}
                    </button>
                    <button
                      data-testid={`memory-delete-${r.id}`}
                      onClick={() => remove(r)}
                      disabled={busy === r.id}
                      className="text-[var(--ink-2)] hover:text-[var(--err)]"
                      title="Delete this rule"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CodingMemory() {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.role === "manager";
  return (
    <AccessGate
      allowed={allowed}
      title="Manager / Admin only"
      message="Firm Coding Memory is managed by admins and managers only."
    >
      <MemoryTable />
    </AccessGate>
  );
}
